import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { exportarExcel, exportarPDFTablas } from '@/lib/exportar';
import { cn } from '@/lib/utils';
import {
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Header,
  type RowData,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
  GripVertical,
  Loader2,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

declare module '@tanstack/react-table' {
  // biome-ignore lint/correctness/noUnusedVariables: module augmentation requires matching generic params
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string;
  }
}

// ---------------------------------------------------------------------------
// Draggable column header cell
// ---------------------------------------------------------------------------
function DraggableHeader<TData extends object>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
    id: header.column.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  return (
    <TableHead ref={setNodeRef} style={style}>
      <div className="flex items-center gap-1">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>

        {header.column.getCanSort() ? (
          <button
            type="button"
            className="flex cursor-pointer select-none items-center gap-0.5"
            onClick={header.column.getToggleSortingHandler()}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getIsSorted() === 'asc' && <ChevronUp className="h-3 w-3" />}
            {header.column.getIsSorted() === 'desc' && <ChevronDown className="h-3 w-3" />}
            {!header.column.getIsSorted() && <ChevronsUpDown className="h-3 w-3 opacity-30" />}
          </button>
        ) : (
          <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
        )}
      </div>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Column visibility panel
// ---------------------------------------------------------------------------
function ColumnVisibilityPanel<TData extends object>({
  table,
}: {
  table: ReturnType<typeof useReactTable<TData>>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const cols = table.getAllColumns().filter((c) => c.getCanHide());

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen((p) => !p)}>
        <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
        Columnas
      </Button>
      {open && (
        <div className="absolute right-0 top-9 z-50 min-w-44 rounded-md border bg-card p-2 shadow-md">
          {cols.map((col) => (
            <label
              key={col.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-primary"
                checked={col.getIsVisible()}
                onChange={(e) => col.toggleVisibility(e.target.checked)}
              />
              {col.columnDef.meta?.label ?? col.id}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataGrid
// ---------------------------------------------------------------------------
interface ExportarConfig {
  titulo: string;
  subtitulo: string;
}

interface DataGridProps<TData extends object> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  isLoading?: boolean;
  pageSize?: number;
  defaultColumnVisibility?: VisibilityState;
  showFilters?: boolean;
  exportar?: ExportarConfig;
  className?: string;
}

export function DataGrid<TData extends object>({
  data,
  columns,
  isLoading,
  pageSize = 50,
  defaultColumnVisibility = {},
  showFilters = true,
  exportar,
  className,
}: DataGridProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(defaultColumnVisibility);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, columnVisibility, columnOrder },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnOrder((prev) => {
      const current = prev.length > 0 ? prev : table.getAllLeafColumns().map((c) => c.id);
      const from = current.indexOf(active.id as string);
      const to = current.indexOf(over.id as string);
      if (from < 0 || to < 0) return prev;
      return arrayMove(current, from, to);
    });
  }

  const totalPaginas = table.getPageCount();
  const paginaActual = table.getState().pagination.pageIndex + 1;
  const filasFiltradas = table.getFilteredRowModel().rows.length;
  const filtrosActivos = columnFilters.length;

  function buildTablaExport() {
    const cols = table.getVisibleLeafColumns();
    const encabezados = cols.map((c) => String(c.columnDef.meta?.label ?? c.id));
    const filas = table.getFilteredRowModel().rows.map((row) =>
      row.getVisibleCells().map((cell) => {
        const v = cell.getValue();
        return v == null ? null : typeof v === 'object' ? String(v) : (v as string | number);
      }),
    );
    return { nombre: exportar?.titulo ?? 'Datos', encabezados, filas };
  }

  function handleExportarExcel() {
    if (!exportar) return;
    exportarExcel(`${exportar.titulo} - ${exportar.subtitulo}`, [buildTablaExport()]);
  }

  async function handleExportarPDF() {
    if (!exportar) return;
    await exportarPDFTablas(exportar.titulo, exportar.subtitulo, [buildTablaExport()]);
  }

  // Column IDs in current display order (for SortableContext)
  const headerIds =
    table
      .getHeaderGroups()[0]
      ?.headers.filter((h) => !h.isPlaceholder)
      .map((h) => h.column.id) ?? [];

  return (
    <div className={cn('space-y-2', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {filasFiltradas} resultado{filasFiltradas !== 1 ? 's' : ''}
          </span>
          {filtrosActivos > 0 && (
            <button
              type="button"
              className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary hover:bg-primary/20"
              onClick={() => setColumnFilters([])}
              title="Limpiar todos los filtros"
            >
              <X className="h-3 w-3" />
              {filtrosActivos} filtro{filtrosActivos !== 1 ? 's' : ''} activo
              {filtrosActivos !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {exportar && (
            <div className="flex items-center gap-1">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Download className="h-3.5 w-3.5" />
                Exportar
              </span>
              <Button size="sm" variant="outline" onClick={handleExportarExcel}>
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Excel
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportarPDF}>
                <FileText className="h-3.5 w-3.5" />
                PDF
              </Button>
            </div>
          )}
          <ColumnVisibilityPanel table={table} />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <SortableContext items={headerIds} strategy={horizontalListSortingStrategy}>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((header) =>
                      header.isPlaceholder ? (
                        <TableHead key={header.id} />
                      ) : (
                        <DraggableHeader key={header.id} header={header} />
                      ),
                    )}
                  </TableRow>
                ))}
              </SortableContext>

              {/* Filter row */}
              {showFilters &&
                table.getHeaderGroups().map((hg) => (
                  <TableRow key={`f-${hg.id}`}>
                    {hg.headers.map((header) => (
                      <TableHead key={`f-${header.id}`} className="py-1">
                        {!header.isPlaceholder && header.column.getCanFilter() ? (
                          <Input
                            className="h-7 text-xs"
                            placeholder="Filtrar…"
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={(e) =>
                              header.column.setFilterValue(e.target.value || undefined)
                            }
                          />
                        ) : null}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : table.getPaginationRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="py-8 text-center text-muted-foreground"
                  >
                    Sin resultados
                  </TableCell>
                </TableRow>
              ) : (
                table.getPaginationRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>

      {/* Pagination */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Página {paginaActual} de {totalPaginas}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
